// One sound player for the whole app: it keeps playing while the parent moves between tabs.
// Two outputs for the same sound (docs/sounds.md): the live Web Audio synth (synth.ts) and a loop rendered from that
// synth (/audio/sounds/<id>.m4a by default, or AUDIO_BASE_URL on the server). The file plays through <audio>, which
// iOS keeps going on the lock screen with Media Session controls, so it is used on iOS, in night mode, when the page
// is hidden and without Web Audio; elsewhere the synth plays. A file that cannot play (offline and not cached yet)
// falls back to the synth. Timer: the last 30 s fade out, then the sound stops (DESIGN.md › Sound and Timer chips).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useSession } from '@/lib/session';
import { SOUNDS, soundById, type SoundId, type TimerMin } from './catalog';
import { synthVoice, type Voice } from './synth';

export type PlaySource = 'baby_button' | 'sheet' | 'sounds_tab' | 'cry_detected';
export type StopReason = 'user' | 'timer' | 'replaced' | 'calm';

type SoundState = {
  soundId: SoundId;
  timerMin: TimerMin;
  playing: boolean;
  /** Epoch ms when the timer ends, or 0 without a timer. */
  endAt: number;
  startedAt: number;
  /** Night mode has the microphone open (iOS audio session 'play-and-record'). */
  listening: boolean;
};

type SoundApi = SoundState & {
  play: (opts?: { id?: SoundId; source?: PlaySource }) => void;
  stop: (reason?: StopReason) => void;
  toggle: (source?: PlaySource) => void;
  choose: (id: SoundId) => void;
  /** Make `id` the Baby Button's sound without starting it (night mode); switches over if a sound is playing. */
  select: (id: SoundId) => void;
  setTimer: (min: TimerMin) => void;
  /** Briefly lower the sound (cry detection listens during the dip). */
  duck: (seconds: number) => void;
  /** Minutes left as shown on the Timer chip: "28 min left", "<1 min left". */
  timerLabel: () => string;
  /** Fade the playing sound out over `seconds`, then stop it (night mode: the baby settled). */
  fadeOut: (seconds: number, reason?: StopReason) => void;
  /** Night mode opened/closed the microphone: keeps iOS in 'play-and-record' so playback does not cut the mic. */
  setListening: (on: boolean) => void;
  /** Unlock audio inside a tap, so a later play() without a tap (cry detected) is allowed. */
  prime: () => void;
};

const Ctx = createContext<SoundApi | null>(null);
const PREFS_KEY = 'bp:sound';
const FADE_MS = 30_000;

function readPrefs(): { soundId: SoundId; timerMin: TimerMin } {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}');
    return { soundId: soundById(p.soundId ?? 'shush').id, timerMin: [15, 30, 45, 60, 0].includes(p.timerMin) ? p.timerMin : 30 };
  } catch {
    return { soundId: 'shush', timerMin: 30 };
  }
}

type FileMap = Partial<Record<SoundId, string>>;
/** Content ids (content/sounds.json) that differ from the app's sound ids. */
const CONTENT_ID: Record<string, SoundId> = { heartbeat: 'heart', lullaby: 'music' };
const FILE_NAME: Partial<Record<SoundId, string>> = { heart: 'heartbeat', music: 'lullaby' };
const FILES_KEY = 'bp:sound:files';
/** Workbox runtime cache of sound files (vite.config.ts). */
const SOUND_CACHE = 'sounds';
/** Until the list has loaded once (a first start offline): the loops shipped with the app. */
const bundledUrl = (id: SoundId) => `/audio/sounds/${FILE_NAME[id] ?? id}.m4a`;
const abs = (url: string) => new URL(url, location.href).href;

function readFiles(): FileMap {
  try {
    return JSON.parse(localStorage.getItem(FILES_KEY) ?? '{}') as FileMap;
  } catch {
    return {};
  }
}

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isMobile = () => isIOS() || matchMedia('(pointer: coarse)').matches;
const hasWebAudio = () => !!(window.AudioContext ?? (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext);
let volumeOk: boolean | undefined;
/** iOS ignores media.volume (the side buttons own it), so a file cannot fade there: the synth takes over the fade. */
const volumeWorks = () => {
  if (volumeOk === undefined) {
    try { const a = new Audio(); a.volume = 0.5; volumeOk = a.volume === 0.5; } catch { volumeOk = false; }
  }
  return volumeOk;
};

export function SoundProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SoundState>(() => ({ ...readPrefs(), playing: false, endAt: 0, startedAt: 0, listening: false }));
  const stateRef = useRef(state);
  stateRef.current = state;

  const ac = useRef<AudioContext | null>(null);
  const master = useRef<GainNode | null>(null);
  const voice = useRef<Voice | null>(null);
  const el = useRef<HTMLAudioElement | null>(null);
  const files = useRef<FileMap>(readFiles());
  /** File URLs (absolute) known to be in the offline cache. */
  const cached = useRef(new Set<string>());
  const warming = useRef(new Set<string>());
  /** Bumped whenever output starts or stops: callbacks of an older start do nothing. */
  const gen = useRef(0);
  const fading = useRef(false);
  /** Length of the running fade (ms): 30 s for the timer, shorter for a quick fade-out. */
  const fadeLen = useRef(FADE_MS);
  const endReason = useRef<StopReason>('timer');
  const listening = useRef(false);
  const duckTimer = useRef(0);
  const { status } = useSession();

  const fileUrl = (id: SoundId) => files.current[id] ?? bundledUrl(id);
  /** Online, or offline with the file cached. */
  const canFile = (url: string) => navigator.onLine || cached.current.has(abs(url));
  /** The playback policy: the file where live Web Audio may stop (iOS lock screen, night mode, a hidden page). */
  const wantFile = (url: string) =>
    canFile(url) && (!hasWebAudio() || isIOS() || listening.current || document.visibilityState !== 'visible');

  const markCached = (urls: string[]) => {
    if (!('caches' in window)) return;
    void caches.open(SOUND_CACHE)
      .then((c) => Promise.all(urls.map((u) => c.match(abs(u)).then((r) => { if (r) cached.current.add(abs(u)); }))))
      .catch(() => undefined);
  };

  /** Puts the whole file in the offline cache once: a plain GET the service worker stores (<audio> only asks for ranges). */
  const warm = (url: string) => {
    const key = abs(url);
    if (cached.current.has(key) || warming.current.has(key) || !navigator.serviceWorker?.controller) return;
    warming.current.add(key);
    void fetch(key)
      .then((r) => (r.ok ? r.blob() : null))
      .then(() => markCached([key]))
      .catch(() => undefined)
      .finally(() => warming.current.delete(key));
  };

  // Sound file URLs. The list needs a signed-in session, so it is (re)loaded once the session is ready; the last list
  // is kept for an offline start. The chosen sound is cached soon after, so tonight it plays offline too.
  useEffect(() => {
    markCached(SOUNDS.map((s) => fileUrl(s.id)));
    if (status !== 'ready') return;
    let live = true;
    api<{ sounds: { id: string; url: string | null }[] }>('/api/content/sounds')
      .then((r) => {
        if (!live) return;
        const next: FileMap = {};
        for (const s of r.sounds ?? []) if (s.url) next[(CONTENT_ID[s.id] ?? s.id) as SoundId] = s.url;
        files.current = next;
        try { localStorage.setItem(FILES_KEY, JSON.stringify(next)); } catch { /* private mode */ }
        markCached(Object.values(next));
        window.setTimeout(() => warm(fileUrl(stateRef.current.soundId)), 3000);
      })
      .catch(() => undefined);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ soundId: state.soundId, timerMin: state.timerMin })); } catch { /* private mode */ }
  }, [state.soundId, state.timerMin]);

  const audio = () => {
    if (!ac.current) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ac.current = new Ctor();
      master.current = ac.current.createGain();
      master.current.gain.value = 0.9;
      master.current.connect(ac.current.destination);
      // iOS suspends ("interrupts") the context when the microphone starts; pick it up again while a sound plays.
      const ctx = ac.current;
      ctx.onstatechange = () => {
        if (ctx.state !== 'running' && ctx.state !== 'closed' && stateRef.current.playing && voice.current && document.visibilityState === 'visible') void ctx.resume().catch(() => undefined);
      };
    }
    void ac.current.resume();
    setSession(listening.current ? 'play-and-record' : 'playback');
    return ac.current;
  };

  const setSession = (type: string) => {
    try {
      const as = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
      if (as && as.type !== type) as.type = type;
    } catch { /* not Safari */ }
  };

  const media = () => (el.current ??= Object.assign(new Audio(), { loop: true, preload: 'auto' }));
  const setSrc = (m: HTMLAudioElement, url: string) => { if (m.src !== abs(url)) m.src = url; };

  const stopOutput = (fade = 0.6) => {
    gen.current++;
    window.clearTimeout(duckTimer.current);
    voice.current?.stop(fade);
    voice.current = null;
    const m = el.current;
    if (m) {
      m.onerror = null;
      m.pause();
      try { m.currentTime = 0; } catch { /* nothing loaded */ }
    }
  };

  /** Live synth at `level` of the master volume (0.9 = full). */
  const startSynth = (id: SoundId, level = 0.9) => {
    const ctx = audio();
    const g = master.current!.gain;
    g.cancelScheduledValues(ctx.currentTime);
    g.setTargetAtTime(level, ctx.currentTime, 0.1);
    voice.current = synthVoice(ctx, master.current!, id);
  };

  /** Timer fade on the synth: to silence by the end (from `from`, when the synth has just taken over from the file). */
  const fadeSynth = (leftMs: number, from?: number) => {
    if (!ac.current || !master.current) return;
    const g = master.current.gain, t = ac.current.currentTime;
    if (from != null) { g.cancelScheduledValues(t); g.setValueAtTime(from, t); }
    g.setTargetAtTime(0, t, Math.max(1, leftMs / 1000) / 4);
  };

  const startFile = (id: SoundId, url: string) => {
    if (listening.current) setSession('play-and-record');
    // unlock Web Audio inside this tap as well, so the synth fallback can start later without one (iOS)
    if (hasWebAudio()) audio();
    const m = media(), g = gen.current;
    setSrc(m, url);
    m.muted = false;
    m.volume = 1;
    // A file that cannot play (offline and not cached, missing, no tap on iOS, stuck loading) falls back to the
    // synthesized sound, so the button never shows "playing" over silence.
    const fallback = () => {
      if (gen.current !== g || voice.current) return;
      m.pause();
      startSynth(id);
    };
    m.onerror = fallback;
    void m.play().catch((e: unknown) => { if ((e as Error | undefined)?.name !== 'AbortError') fallback(); });
    window.setTimeout(() => { if (gen.current === g && !voice.current && !m.paused && m.readyState < 3) fallback(); }, 6000);
  };

  const startOutput = (id: SoundId) => {
    stopOutput(0.4);
    fading.current = false;
    endReason.current = 'timer';
    const url = fileUrl(id);
    if (wantFile(url)) startFile(id, url);
    else startSynth(id);
    warm(url); // next time it also plays offline
    const s = soundById(id);
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({ title: s.name, artist: 'Baby Plan', artwork: [{ src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }] });
      navigator.mediaSession.playbackState = 'playing';
    }
  };

  const stop = useCallback((reason: StopReason = 'user') => {
    const cur = stateRef.current;
    if (!cur.playing) return;
    stopOutput(reason === 'timer' ? 0.3 : 0.6);
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    endReason.current = 'timer';
    track(reason === 'timer' ? 'sound_timer_end' : 'sound_stop', { sound: cur.soundId, timer_min: cur.timerMin, seconds: Math.round((Date.now() - cur.startedAt) / 1000), reason }, 'sounds', cur.soundId);
    setState((s) => ({ ...s, playing: false, endAt: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = useCallback((opts: { id?: SoundId; source?: PlaySource } = {}) => {
    const cur = stateRef.current;
    const id = opts.id ?? cur.soundId;
    startOutput(id);
    const now = Date.now();
    track('sound_play', { sound: id, timer_min: cur.timerMin, source: opts.source ?? 'baby_button' }, 'sounds', id);
    setState((s) => ({ ...s, soundId: id, playing: true, startedAt: now, endAt: s.timerMin ? now + s.timerMin * 60_000 : 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback((source: PlaySource = 'baby_button') => {
    if (stateRef.current.playing) stop('user'); else play({ source });
  }, [play, stop]);

  /** Tapping a sound selects and plays it (DESIGN.md › Sound sheet). */
  const choose = useCallback((id: SoundId) => {
    play({ id, source: 'sheet' });
  }, [play]);

  const select = useCallback((id: SoundId) => {
    if (stateRef.current.playing) { if (stateRef.current.soundId !== id) play({ id, source: 'sheet' }); return; }
    setState((s) => ({ ...s, soundId: soundById(id).id }));
  }, [play]);

  /** Changing the timer while a sound plays restarts the countdown from now. */
  const setTimer = useCallback((min: TimerMin) => {
    fading.current = false;
    endReason.current = 'timer';
    if (master.current && ac.current) {
      master.current.gain.cancelScheduledValues(ac.current.currentTime);
      master.current.gain.setTargetAtTime(0.9, ac.current.currentTime, 0.1);
    }
    if (el.current) el.current.volume = 1;
    setState((s) => ({ ...s, timerMin: min, endAt: s.playing && min ? Date.now() + min * 60_000 : 0 }));
  }, []);

  const duck = useCallback((seconds: number) => {
    if (!stateRef.current.playing) return;
    if (el.current && !voice.current) {
      const m = el.current, src = m.src;
      m.pause();
      window.clearTimeout(duckTimer.current);
      // resume only the same file, and only if nothing else started meanwhile
      duckTimer.current = window.setTimeout(() => { if (stateRef.current.playing && !voice.current && m.src === src) void m.play().catch(() => undefined); }, seconds * 1000);
      return;
    }
    const ctx = ac.current, g = master.current;
    if (!ctx || !g) return;
    const t = ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setTargetAtTime(0.0, t, 0.08);
    g.gain.setTargetAtTime(0.9, t + seconds, 0.08);
  }, []);

  // Timer: checked every second (JS keeps running on iOS while audio plays; checked again on resume).
  useEffect(() => {
    if (!state.playing || !state.endAt) return;
    const check = () => {
      const left = stateRef.current.endAt - Date.now();
      if (left <= FADE_MS && !fading.current) {
        fading.current = true;
        fadeLen.current = Math.max(1000, left);
        if (voice.current) fadeSynth(left);
      }
      const m = el.current;
      if (fading.current && m && !voice.current && !m.paused) {
        const level = Math.max(0, Math.min(1, left / fadeLen.current));
        if (volumeWorks()) m.volume = level;
        else if (document.visibilityState === 'visible' && hasWebAudio() && left > 1500) {
          // iOS: the file cannot fade, so the synth (same sound, same level) takes over for the fade
          startSynth(stateRef.current.soundId, 0.9 * level);
          fadeSynth(left, 0.9 * level);
          window.setTimeout(() => { if (voice.current) m.pause(); }, 150);
        }
        // hidden on iOS: no fade is possible there; the sound stops when the time is up
      }
      if (left <= 0) stop(endReason.current);
      else setState((s) => ({ ...s })); // refresh the countdown label
    };
    const iv = window.setInterval(check, 1000);
    const first = window.setTimeout(check, 0); // a short fade-out (2 s) starts now, not a second later
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(iv); clearTimeout(first); document.removeEventListener('visibilitychange', check); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.playing, state.endAt, stop]);

  // Page hidden (lock, app switch) on a phone while the synth plays: hand over to the file, which the OS keeps
  // playing in the background. The synth stops only once the file really plays; if it cannot, the synth stays.
  const swapToFile = () => {
    const s = stateRef.current, v = voice.current;
    if (!s.playing || !v || !isMobile()) return;
    if (fading.current && !volumeWorks()) return; // iOS mid-fade: a file would jump back to full volume
    const url = fileUrl(s.soundId);
    if (!canFile(url)) return;
    const m = media(), g = gen.current;
    setSrc(m, url);
    m.muted = false;
    m.volume = fading.current ? Math.max(0, Math.min(1, (s.endAt - Date.now()) / fadeLen.current)) : 1;
    m.onerror = null;
    void m.play()
      .then(() => {
        if (gen.current !== g || voice.current !== v) return;
        v.stop(0.3);
        voice.current = null;
      })
      .catch(() => undefined);
  };

  // Back in the app after a lock, a call or Siri: iOS suspended the audio while the page was hidden,
  // and nothing would restart it. Pick the sound up again if it should still be playing.
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === 'hidden') { swapToFile(); return; }
      if (!stateRef.current.playing) return;
      if (voice.current && ac.current && ac.current.state !== 'running' && ac.current.state !== 'closed') void ac.current.resume().catch(() => undefined);
      else if (!voice.current && el.current?.paused) void el.current.play().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', resume);
    return () => { document.removeEventListener('visibilitychange', resume); window.removeEventListener('pageshow', resume); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock-screen controls (the file player shows them on iOS and Android)
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const set = (a: MediaSessionAction, fn: () => void) => { try { navigator.mediaSession.setActionHandler(a, fn); } catch { /* unsupported */ } };
    set('play', () => play({ source: 'baby_button' }));
    set('pause', () => stop('user'));
    set('stop', () => stop('user'));
  }, [play, stop]);

  const timerLabel = useCallback(() => {
    const s = stateRef.current;
    if (!s.timerMin) return 'No timer';
    if (!s.playing || !s.endAt) return `${s.timerMin} min`;
    const left = Math.max(0, s.endAt - Date.now()) / 60_000;
    return left < 1 ? '<1 min left' : `${Math.ceil(left)} min left`;
  }, []);

  /** Reuses the timer's fade: the end moves to now + seconds; the last 30 s (or all of a shorter fade) fade out. */
  const fadeOut = useCallback((seconds: number, reason: StopReason = 'calm') => {
    if (!stateRef.current.playing) return;
    endReason.current = reason;
    fading.current = false;
    setState((s) => ({ ...s, endAt: Date.now() + Math.max(1, seconds) * 1000 }));
  }, []);

  const setListening = useCallback((on: boolean) => {
    listening.current = on;
    setSession(on ? 'play-and-record' : stateRef.current.playing ? 'playback' : 'auto');
    setState((s) => (s.listening === on ? s : { ...s, listening: on }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prime = useCallback(() => {
    try {
      const ctx = audio();
      // a silent one-sample buffer unlocks output on older iOS
      const src = ctx.createBufferSource(); src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate); src.connect(ctx.destination); src.start();
    } catch { /* no Web Audio: nothing to unlock */ }
    // The file player too: a muted start inside the tap lets a cry start it later without a tap (iOS).
    // It also loads the chosen sound and puts it in the offline cache for the night.
    const s = stateRef.current;
    const url = fileUrl(s.soundId);
    warm(url);
    if (s.playing || !canFile(url)) return;
    const m = media(), g = gen.current;
    setSrc(m, url);
    m.muted = true;
    void m.play()
      .then(() => { if (gen.current === g) m.pause(); })
      .catch(() => undefined)
      .finally(() => { if (gen.current === g) m.muted = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SoundApi>(
    () => ({ ...state, play, stop, toggle, choose, select, setTimer, duck, timerLabel, fadeOut, setListening, prime }),
    [state, play, stop, toggle, choose, select, setTimer, duck, timerLabel, fadeOut, setListening, prime],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSound(): SoundApi {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSound outside SoundProvider');
  return s;
}
